/**
 * Phase 21.4 Batch 3D — Public Hall of Champions (/hall-of-champions).
 *
 * Anonymous & authenticated visitors both see it. Feature-flag gated
 * at the API — when Contest Center is OFF, this page shows the
 * "Coming Soon" placeholder.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";
import { getProfilePhotoUrl, getCoverUrl } from "@/lib/mediaUrls";

const positionLabel = (p) => (p === 1 ? "🥇 1st" : p === 2 ? "🥈 2nd" : p === 3 ? "🥉 3rd" : `#${p}`);
const positionTone = (p) =>
  p === 1
    ? "bg-amber-500/20 border-amber-500/60 text-amber-100"
    : p === 2
      ? "bg-slate-400/20 border-slate-400/60 text-slate-100"
      : "bg-orange-600/20 border-orange-500/60 text-orange-100";

const HallOfChampionsPage = () => {
  const [enabled, setEnabled] = useState(null);
  const [facets, setFacets] = useState({ years: [], contests: [], entry_types: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("");
  const [contestId, setContestId] = useState("");
  const [entryType, setEntryType] = useState("");
  const [position, setPosition] = useState("");

  const loadFacets = useCallback(async () => {
    try {
      const r = await api.get("/hall-of-champions/facets");
      if (r.data?.enabled === false) {
        setEnabled(false);
      } else {
        setEnabled(true);
      }
      setFacets({
        years: r.data?.years || [],
        contests: r.data?.contests || [],
        entry_types: r.data?.entry_types || [],
      });
    } catch { /* ignore */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (year) params.year = year;
      if (contestId) params.contest_id = contestId;
      if (entryType) params.entry_type = entryType;
      if (position) params.position = position;
      const r = await api.get("/hall-of-champions", { params });
      if (r.data?.enabled === false) {
        setEnabled(false);
        setRows([]);
      } else {
        setEnabled(true);
        setRows(r.data?.champions || []);
      }
    } catch (e) {
      toast.error("Failed to load champions");
    } finally {
      setLoading(false);
    }
  }, [year, contestId, entryType, position]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const hasFilters = year || contestId || entryType || position;
  const resetFilters = () => { setYear(""); setContestId(""); setEntryType(""); setPosition(""); };

  // Phase 21.4 Batch 3H — Section-based categorization (by source).
  // Order (fixed): Spotlight Artists → In The Booth → Beat of the Week
  //  → Holiday/Theme Challenges → Contest Center Winners → Other.
  const SECTIONS = useMemo(
    () => [
      { key: "weekly_spotlight", title: "🌟 Spotlight Artists", icon: "🌟" },
      { key: "in_the_booth", title: "🎙 In The Booth", icon: "🎙" },
      { key: "beat_of_the_week", title: "🎧 Beat of the Week", icon: "🎧" },
      { key: "holiday_theme", title: "🎉 Holiday / Theme Challenges", icon: "🎉" },
      { key: "contest_center", title: "🏆 Contest Center Winners", icon: "🏆" },
      { key: "other", title: "🏛 Other Historical Winners", icon: "🏛" },
    ],
    [],
  );

  const bucketForRow = useCallback((r) => {
    const cat = (r.category || "").toLowerCase();
    if (cat === "weekly_spotlight") return "weekly_spotlight";
    if (cat === "in_the_booth") return "in_the_booth";
    if (cat === "beat_of_the_week") return "beat_of_the_week";
    // Holiday / Theme detected from contest slug/title, or a category tag
    const contestTitle = (r.contest?.title || r.contest_title || "").toLowerCase();
    const contestSlug = (r.contest?.slug || r.contest_slug || "").toLowerCase();
    const holidayHit = /(holiday|christmas|thanksgiving|halloween|valentine|easter|new[-_ ]?year|theme)/;
    if (cat === "holiday" || cat === "theme" || holidayHit.test(contestTitle) || holidayHit.test(contestSlug)) {
      return "holiday_theme";
    }
    if (cat === "contest" || r.contest?.contest_id || r.contest_id) return "contest_center";
    return "other";
  }, []);

  const sectionedGroups = useMemo(() => {
    const buckets = {
      weekly_spotlight: [],
      in_the_booth: [],
      beat_of_the_week: [],
      holiday_theme: [],
      contest_center: [],
      other: [],
    };
    for (const r of rows) buckets[bucketForRow(r)].push(r);

    return SECTIONS.map((sec) => {
      const items = buckets[sec.key] || [];
      // Sub-group by contest/spotlight/BOTW week
      const sub = {};
      for (const r of items) {
        let subKey = "";
        let subTitle = "";
        let subLink = null;
        if (sec.key === "weekly_spotlight") {
          subKey = r.spotlight_id || `sw:${r.contest_title || ""}`;
          subTitle = r.contest_title || "Spotlight";
        } else if (sec.key === "beat_of_the_week") {
          subKey = r.botw_week_id || `botw:${r.contest_title || r.hof_id}`;
          subTitle = r.contest_title || `Beat of the Week`;
        } else if (sec.key === "contest_center" && (r.contest?.contest_id || r.contest_id)) {
          subKey = r.contest?.contest_id || r.contest_id;
          subTitle = r.contest?.title || r.contest_title || "Contest";
          if (r.contest?.slug) subLink = `/contests/${r.contest.slug}`;
        } else {
          subKey = `${sec.key}:${r.contest_title || r.category || "misc"}`;
          subTitle = r.contest_title || sec.title;
        }
        sub[subKey] = sub[subKey] || { key: subKey, title: subTitle, link: subLink, rows: [] };
        sub[subKey].rows.push(r);
      }
      return { ...sec, groups: Object.values(sub), total: items.length };
    });
  }, [rows, SECTIONS, bucketForRow]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="hof-page">
      <SharedNavigation />
      <div className="max-w-5xl mx-auto pt-24 px-4 pb-16 space-y-6">
        {/* Hero */}
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🏛 RGM Studio</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mt-1" data-testid="hof-page-title">
            Hall of Champions
          </h1>
          <p className="text-base text-gray-400 mt-2 max-w-2xl">
            Every contest winner, forever. Their placements are permanent — even when contests close.
          </p>
          <div className="mt-4">
            <Link
              to="/contests"
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white text-xs font-black hover:bg-white/20 transition-colors"
              data-testid="hof-page-back-to-contests"
            >
              ← Contest Center
            </Link>
          </div>
        </div>

        {enabled === false && (
          <div
            className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-center"
            data-testid="hof-page-flag-off"
          >
            <p className="text-4xl mb-3">🏛</p>
            <h2 className="text-2xl font-black text-white mb-2">Coming Soon</h2>
            <p className="text-sm text-gray-400 max-w-md mx-auto">
              The Hall of Champions opens when the first contest crowns its winners.
            </p>
          </div>
        )}

        {enabled && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center" data-testid="hof-page-filters">
              <select
                value={year} onChange={(e) => setYear(e.target.value)}
                className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                data-testid="hof-page-filter-year"
              >
                <option value="" className="bg-[#13131a]">All years</option>
                {facets.years.map((y) => <option key={y} value={y} className="bg-[#13131a]">{y}</option>)}
              </select>
              <select
                value={contestId} onChange={(e) => setContestId(e.target.value)}
                className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm min-w-[180px]"
                data-testid="hof-page-filter-contest"
              >
                <option value="" className="bg-[#13131a]">All contests</option>
                {facets.contests.map((c) => (
                  <option key={c.contest_id} value={c.contest_id} className="bg-[#13131a]">
                    {c.title || c.contest_id} ({c.count})
                  </option>
                ))}
              </select>
              <select
                value={entryType} onChange={(e) => setEntryType(e.target.value)}
                className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                data-testid="hof-page-filter-entry-type"
              >
                <option value="" className="bg-[#13131a]">All types</option>
                {facets.entry_types.map((t) => (
                  <option key={t.entry_type} value={t.entry_type} className="bg-[#13131a]">
                    {t.entry_type} ({t.count})
                  </option>
                ))}
              </select>
              <select
                value={position} onChange={(e) => setPosition(e.target.value)}
                className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                data-testid="hof-page-filter-position"
              >
                <option value="" className="bg-[#13131a]">All placements</option>
                <option value="1" className="bg-[#13131a]">🥇 1st</option>
                <option value="2" className="bg-[#13131a]">🥈 2nd</option>
                <option value="3" className="bg-[#13131a]">🥉 3rd</option>
              </select>
              {hasFilters && (
                <button
                  onClick={resetFilters}
                  className="text-xs text-gray-400 hover:text-white underline ml-2"
                  data-testid="hof-page-filter-reset"
                >
                  Reset
                </button>
              )}
            </div>

            {loading && <p className="text-sm text-gray-500" data-testid="hof-page-loading">Loading…</p>}
            {!loading && rows.length === 0 && (
              <div
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center"
                data-testid="hof-page-empty"
              >
                <p className="text-4xl mb-3">🏛</p>
                <p className="text-white font-black">No champions match those filters yet.</p>
              </div>
            )}

            {!loading && sectionedGroups.some((s) => s.total > 0) && (
              <div className="space-y-8" data-testid="hof-page-list">
                {sectionedGroups.map((sec) => {
                  if (sec.total === 0) return null;
                  return (
                    <div
                      key={sec.key}
                      className="space-y-3"
                      data-testid={`hof-page-section-${sec.key}`}
                    >
                      <div className="flex items-end justify-between gap-3 border-b border-white/10 pb-2">
                        <h2 className="text-2xl font-black text-white flex items-center gap-2">
                          {sec.title}
                        </h2>
                        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-black">
                          {sec.total} winner{sec.total === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="space-y-4">
                        {sec.groups.map((grp) => (
                          <section
                            key={`${sec.key}:${grp.key}`}
                            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5"
                            data-testid={`hof-page-group-${sec.key}-${grp.key}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                              <h3 className="text-base font-black text-white flex items-center gap-2">
                                {grp.title}
                              </h3>
                              {grp.link && (
                                <Link
                                  to={grp.link}
                                  className="text-xs text-purple-300 hover:underline"
                                >
                                  View contest →
                                </Link>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {grp.rows.map((r) => (
                                <Link
                                  key={r.hof_id}
                                  to={`/artist/${r.user_id}`}
                                  className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-black/50 hover:border-amber-500/50 hover:bg-black/70 transition-all"
                                  data-testid={`hof-page-champion-${r.hof_id}`}
                                >
                                  {(r.winner_cover_url || r.entry_snapshot?.cover_art) ? (
                                    <img
                                      // iter_186 — prefer canonical 1500×1500
                                      // Cloudinary winner cover; fallback to
                                      // legacy entry_snapshot.cover_art for
                                      // pre-iter_184 rows. Both go through
                                      // getCoverUrl so bare-filename / avatar
                                      // stains fall back to the branded
                                      // placeholder instead of a profile
                                      // avatar being rendered as the track
                                      // cover.
                                      src={getCoverUrl({
                                        cover_url: r.winner_cover_url
                                          || r.entry_snapshot?.cover_url
                                          || r.entry_snapshot?.cover_art,
                                        cover_art: r.entry_snapshot?.cover_art,
                                      })}
                                      alt={r.entry_snapshot?.title || ""}
                                      className="w-14 h-14 rounded-lg object-cover border border-white/10 flex-shrink-0"
                                      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                                      data-testid={`hof-page-cover-${r.hof_id}`}
                                    />
                                  ) : (
                                    <img
                                      src={getCoverUrl(null)}
                                      alt="Track cover unavailable"
                                      className="w-14 h-14 rounded-lg object-cover border border-white/10 flex-shrink-0"
                                      data-testid={`hof-page-cover-placeholder-${r.hof_id}`}
                                    />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span
                                        className={`inline-flex items-center justify-center rounded-full border font-black text-[10px] px-2 py-0.5 ${positionTone(r.position)}`}
                                        data-testid={`hof-page-placement-${r.hof_id}`}
                                      >
                                        {positionLabel(r.position)}
                                      </span>
                                      {r.entry_type && (
                                        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-black">{r.entry_type}</span>
                                      )}
                                    </div>
                                    <p className="text-white text-sm font-black truncate mt-0.5">
                                      {r.artist?.artist_name || r.user_id}
                                    </p>
                                    <p className="text-[11px] text-gray-400 truncate">
                                      {r.entry_snapshot?.title || r.entry_ref_id}
                                    </p>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HallOfChampionsPage;
